/* Product-analytics tracking wrapper (Amplitude / Ampli).
 *
 * The thin layer the app calls instead of touching the generated `ampli`
 * client directly. It:
 *   - auto-injects the Studio standard properties on every event
 *     (`app`, `page`, `path`, `workspaceId`, referrer; `host`/`sessionId` are
 *     auto-attached by the Amplitude browser SDK so we don't duplicate them),
 *   - guards every call on `ampli.isLoaded` so analytics is a no-op (never
 *     throws) when no API key is configured — mock mode, tests, Storybook.
 *
 * Event taxonomy: Notion "📊 Tokenwise". `app` is "tokenwise" (the PRODUCT
 * tag — see ../product), which distinguishes this app from the other Studio
 * sub-apps sharing the studio.ai21.com host. */

import { ampli, type IdentifyProperties } from "../integrations/ampli";
import {
  AuthCompleted,
  AuthProviderFailed,
  AuthProviderSucceeded,
} from "../integrations/ampli/authEvents";
import { pushGtmEvent } from "../integrations/gtm";
import { PRODUCT } from "../product";
import { getActiveWorkspaceId } from "../identity/active";
import type { ApiError, ApiErrorCode } from "./types";
import { toApiError, friendlyError } from "./errors";
import { logger } from "../services/telemetry";

/** Page context published by the page-tracking hook (usePageTracking) so that
 *  non-React callers (the client layer, error paths) can tag events with the
 *  current page/scope/agentId. Updated on every navigation. */
interface PageContext {
  page?: string;
  scope?: string;
  agentId?: string;
  path?: string;
  referrerPagePath?: string;
  referrerPageHost?: string;
}

let pageContext: PageContext = {};

/** Replace the current page context. Called by usePageTracking on navigation. */
export function setPageContext(ctx: PageContext): void {
  pageContext = ctx;
}

/** The standard properties attached to every event. `scope`/`agentId` are not
 *  in the published plan yet (pending PDS sync), so they ride along untyped;
 *  the cast at each call site keeps that isolated here. */
function commonContext() {
  return {
    app: PRODUCT,
    workspaceId: getActiveWorkspaceId() ?? "",
    ...(pageContext.page ? { page: pageContext.page } : {}),
    ...(pageContext.path ? { path: pageContext.path } : {}),
    ...(pageContext.scope ? { scope: pageContext.scope } : {}),
    ...(pageContext.agentId ? { agentId: pageContext.agentId } : {}),
  };
}

// ---- identity ---------------------------------------------------------------

/** Identify the signed-in user and set the organization as the group property
 *  (`organization_id` — the existing Studio group dimension), so every event
 *  is attributed to the user and their org. Called from AuthTelemetryBridge on
 *  the authenticated session.
 *
 *  Sets the SDK's ambient user_id (not just the identify event's) so EVERY
 *  later event carries it — `ampli.identify` alone only tags that one call, so
 *  the user_id never stuck and subsequent events stayed anonymous. Keeping the
 *  same device_id across the switch is what merges the pre-auth anonymous
 *  session into the identified user (their story, before and after login). */
export function identifyUser(userId: string, organizationId?: string): void {
  if (!ampli.isLoaded) return;

  ampli.client.setUserId(userId);

  const properties: IdentifyProperties = {};
  if (organizationId) properties.organization_id = organizationId;

  ampli.identify(userId, properties);
}

/** Clear the identified user from the Amplitude context on logout: `reset`
 *  wipes the user_id and regenerates the device_id, so the next person on this
 *  browser starts a fresh anonymous session rather than inheriting the previous
 *  user's identity. */
export function resetUser(): void {
  if (!ampli.isLoaded) return;

  ampli.client.reset();
}

// ---- events -----------------------------------------------------------------

/** PageEntered — fired on navigation into a dashboard page (usePageTracking). */
export function trackPageEntered(): void {
  if (!ampli.isLoaded) return;

  ampli.pageEntered({
    ...commonContext(),
    ...(pageContext.referrerPagePath
      ? { referrerPagePath: pageContext.referrerPagePath }
      : {}),
    ...(pageContext.referrerPageHost
      ? { referrerPageHost: pageContext.referrerPageHost }
      : {}),
    // `scope`/`agentId` are not in the PageEntered schema yet (pending PDS
    // sync); send them anyway so they're ready once the plan catches up.
  } as Parameters<typeof ampli.pageEntered>[0]);
}

/** ButtonPressed — generic press event for primary CTAs. `entity`/`itemId`
 *  describe what the button acts on (e.g. "agent" + the agent id). Fires
 *  alongside any semantic event (AgentCreated, etc.) to give a uniform
 *  button-funnel view. */
export function trackButtonPressed(
  buttonText: string,
  entity?: string,
  itemId?: string,
): void {
  if (!ampli.isLoaded) return;

  ampli.buttonPressed({
    app: PRODUCT,
    workspaceId: ws(),
    buttonText,
    ...(entity ? { entity } : {}),
    ...(itemId ? { itemId } : {}),
    ...(pageContext.page ? { page: pageContext.page } : {}),
  });
}

// ---- agent lifecycle --------------------------------------------------------

/** AgentCreated — fired from the agent client decorator on create.
 *
 *  Also a marketing conversion (OPSS-249). The GTM push sits ABOVE the
 *  `isLoaded` guard — like logger.error in trackApiFailed — so marketing
 *  conversions don't silently depend on Amplitude being configured. It carries
 *  `agentKind` (a closed enum) but never `agentId`, which is a user-chosen
 *  string. Fires on every creation, per the ticket, not only the first. */
export function trackAgentCreated(agentId: string, agentKind: string): void {
  pushGtmEvent("AgentCreated", { agentKind });

  if (!ampli.isLoaded) return;

  ampli.agentCreated({ app: PRODUCT, workspaceId: ws(), agentId, agentKind });
}

/** AgentUpdated — fired from the agent client decorator on update. */
export function trackAgentUpdated(
  agentId: string,
  agentKind: string,
  enabled: boolean,
): void {
  if (!ampli.isLoaded) return;

  ampli.agentUpdated({
    app: PRODUCT,
    workspaceId: ws(),
    agentId,
    agentKind,
    enabled,
  });
}

/** AgentDeleted — fired from the agent client decorator on remove. */
export function trackAgentDeleted(agentId: string): void {
  if (!ampli.isLoaded) return;

  ampli.agentDeleted({ app: PRODUCT, workspaceId: ws(), agentId });
}

// ---- config lifecycle -------------------------------------------------------

/** ConfigCreated — fired from the config client decorator on create (covers
 *  the agent-create copy-config flow too). */
export function trackConfigCreated(configId: string): void {
  if (!ampli.isLoaded) return;

  ampli.configCreated({ app: PRODUCT, workspaceId: ws(), configId });
}

/** ConfigUpdated — fired from the config client decorator on update. */
export function trackConfigUpdated(configId: string): void {
  if (!ampli.isLoaded) return;

  ampli.configUpdated({ app: PRODUCT, workspaceId: ws(), configId });
}

/** ConfigDeleted — fired from the config client decorator on remove (covers
 *  the agent-delete cascade too). */
export function trackConfigDeleted(configId: string): void {
  if (!ampli.isLoaded) return;

  ampli.configDeleted({ app: PRODUCT, workspaceId: ws(), configId });
}

// ---- onboarding connect funnel ----------------------------------------------

/** The two ends of the CLI connect step: `command` is arrival, `connected` is
 *  the gateway seeing the agent's first request.
 *
 *  The stages in between were dropped — each was unlocked by a copy button that
 *  already emits its own ButtonPressed, so they only restated it. These two
 *  can't be read off ButtonPressed at all: nothing is pressed to arrive, and it
 *  is traffic rather than the reader that reports the connection. They are the
 *  funnel's denominator and its conversion; `/welcome` is a single route, so no
 *  page view separates this step from the rest of the wizard. */
export type ConnectRevealStage = "command" | "connected";

/** ConnectStepRevealed — the reader reached the connect step, or got connected.
 *
 *  Not in the generated Ampli client (the plan is behind), so it goes out
 *  through the raw client like the insight events above; Amplitude ingests and
 *  charts unplanned events, and the plan can adopt it later. */
export function trackConnectStepRevealed(
  stage: ConnectRevealStage,
  agentId: string,
): void {
  if (!ampli.isLoaded) return;

  ampli.client.track("ConnectStepRevealed", {
    app: PRODUCT,
    workspaceId: ws(),
    agentId,
    stage,
    ...(pageContext.page ? { page: pageContext.page } : {}),
  });
}

// ---- insights ---------------------------------------------------------------

/* The insight funnel (OPSS-208): InsightShown → InsightActionPressed.
 *
 * Every other step of an insight's life is already a row in the BFF's insights
 * table (authored, published, and each terminal state). What nothing else can
 * record is whether a published insight was ever actually put in front of a
 * human — so InsightShown is the denominator for apply/dismiss rates, and
 * InsightActionPressed joins to it on `insightId`.
 *
 * Property names and value sets come from the merged Amplitude schemas
 * (studio-amplitude-bq#11); they carry ONLY the properties those schemas
 * declare — note `path` is absent even though most events here send it.
 *
 * Neither event is in the generated Ampli client yet (`ampli.json` is at plan
 * v4.0.0), so both go out through the raw client. Amplitude ingests and charts
 * unplanned events, and the plan can adopt them later; keeping every emission
 * in this file makes the swap to `ampli.insightShown(...)` after the next
 * `npm run ampli-pull` a one-file change.
 *
 * The four insight buttons also keep firing ButtonPressed with their original
 * `buttonText` ("Apply savings", "Dismiss", "Helpful", "Not helpful") — those
 * are live in Amplitude charts, so they are neither renamed nor dropped. */

/** The insight an event is about — the shared dimensions of both events.
 *
 *  `kind` must be read through `insightKind()` at the call site rather than off
 *  the wire payload, so a response predating OPSS-283 reports the behaviour the
 *  user actually got instead of an empty string. */
export interface InsightRef {
  insightId: string;
  agentId: string;
  /** `config_change` | `advisory`. */
  kind: string;
}

/** What the user did to an insight. `acknowledge` is the advisory card's 👍 —
 *  its terminal positive action, since an advisory has no Apply. */
export type InsightAction = "apply" | "dismiss" | "acknowledge";

/* InsightShown is deduped per insight per session: the schema says so, and a
 * denominator only means anything if it counts insights rather than scroll
 * events (cards remount on every refetch and tab switch). Tab-scoped — a reload
 * starts a new Amplitude session, which may legitimately re-report. */
const shownInsights = new Set<string>();

/** Test seam: the dedupe set deliberately outlives any single render tree. */
export function resetInsightShownDedupe(): void {
  shownInsights.clear();
}

/** InsightShown — an insight suggestion reached the viewport. Fires on
 *  intersection, not render (see `useOnVisible`), so a card below the fold
 *  doesn't inflate the denominator. `position` is its 0-based rank in the rail
 *  it was shown in. */
export function trackInsightShown(ref: InsightRef, position: number): void {
  if (!ampli.isLoaded) return;

  if (shownInsights.has(ref.insightId)) return;

  shownInsights.add(ref.insightId);
  ampli.client.track("InsightShown", { ...insightProps(ref), position });
}

/** InsightActionPressed — the user acted on an insight.
 *
 *  Fires at the click, so it records intent rather than the server's
 *  confirmation: the transition itself lands in the BFF table, which makes a
 *  press with no matching state change there a failed action. `source` is
 *  "manual" until something other than a human can act on an insight.
 *  `dismissReason` is set only where the UI captures one — today that is the
 *  advisory 👎, which dismisses with reason `not_helpful`. */
export function trackInsightActionPressed(
  ref: InsightRef,
  action: InsightAction,
  dismissReason?: string,
): void {
  if (!ampli.isLoaded) return;

  ampli.client.track("InsightActionPressed", {
    ...insightProps(ref),
    action,
    source: "manual",
    ...(dismissReason ? { dismissReason } : {}),
  });
}

/** Properties shared by both insight events. */
function insightProps(ref: InsightRef) {
  return {
    app: PRODUCT,
    workspaceId: ws(),
    agentId: ref.agentId,
    insightId: ref.insightId,
    kind: ref.kind,
    ...(pageContext.page ? { page: pageContext.page } : {}),
  };
}

// ---- filters ----------------------------------------------------------------

/** TimeRangeChanged — fired when the dashboard time range changes. */
export function trackTimeRangeChanged(
  rangePreset: string,
  prevRangePreset?: string,
): void {
  if (!ampli.isLoaded) return;

  ampli.timeRangeChanged({
    app: PRODUCT,
    workspaceId: ws(),
    rangePreset,
    ...(prevRangePreset ? { prevRangePreset } : {}),
    ...(pageContext.page ? { page: pageContext.page } : {}),
  });
}

/** FilterChanged — fired when a non-time filter changes (config / routed). */
export function trackFilterChanged(
  filterType: "config" | "routed",
  value?: string,
): void {
  if (!ampli.isLoaded) return;

  ampli.filterChanged({
    app: PRODUCT,
    workspaceId: ws(),
    filterType,
    ...(value !== undefined ? { value } : {}),
    ...(pageContext.page ? { page: pageContext.page } : {}),
  });
}

// ---- auth -------------------------------------------------------------------

/* Auth method/type follow the Studio enum conventions. The app offers Google
 * (redirect) and email/password sign-in; values are UPPERCASE for the enum. */
export type AuthMethod = "GOOGLE" | "EMAIL_PASSWORD";
export type AuthType = "LOGIN" | "SIGN_UP";

/** AuthStarted — fired when an auth attempt begins, i.e. the user presses an
 *  auth-method button (before the Firebase exchange resolves). */
export function trackAuthStarted(
  authMethod: AuthMethod,
  authType: AuthType,
): void {
  if (!ampli.isLoaded) return;

  ampli.authStarted({
    app: PRODUCT,
    authMethod,
    authType,
    ...(pageContext.page ? { page: pageContext.page } : {}),
  });
}

/* ---- auth funnel (AuthStarted -> AuthProviderSucceeded -> AuthCompleted) ----
 *
 * The three below are not on the generated client — see
 * ../integrations/ampli/authEvents for why — so they go through `ampli.track`,
 * which takes the same BaseEvent shape the generated methods build.
 *
 * The invariant the auth funnel depends on: every AuthStarted is terminated by
 * exactly one AuthCompleted or AuthProviderFailed. It is enforced in
 * auth/authAttempt.ts, which owns the in-flight attempt; these functions are
 * the dumb emitters and must not be called directly from screens. */

/** The properties every auth-funnel event carries, matching the generated
 *  AuthStarted so all four line up in one funnel. */
function authContext(authMethod: AuthMethod, authType: AuthType) {
  return {
    app: PRODUCT,
    authMethod,
    authType,
    ...(pageContext.page ? { page: pageContext.page } : {}),
    ...(pageContext.path ? { path: pageContext.path } : {}),
  };
}

/** AuthProviderSucceeded — the identity provider (Firebase, incl. the Google
 *  round trip) returned a credential. Not terminal: the identity exchange
 *  still has to land before the attempt counts as complete. */
export function trackAuthProviderSucceeded(
  authMethod: AuthMethod,
  authType: AuthType,
): void {
  if (!ampli.isLoaded) return;

  ampli.track(new AuthProviderSucceeded(authContext(authMethod, authType)));
}

/** AuthProviderFailed — terminal failure of an auth attempt, whichever leg it
 *  died on. There is deliberately no separate "identity exchange failed"
 *  event: the plan offers this one terminal failure, and a funnel with two
 *  different failure events would under-count either way. */
export function trackAuthProviderFailed(
  authMethod: AuthMethod,
  authType: AuthType,
  err: unknown,
  fallbackCode?: string,
): void {
  if (!ampli.isLoaded) return;

  const api: ApiError | undefined = toApiError(err);
  const code = api?.code ?? firebaseErrorCode(err) ?? fallbackCode;

  ampli.track(
    new AuthProviderFailed({
      ...authContext(authMethod, authType),
      ...(code ? { errorCode: code } : {}),
      errorMessage: friendlyError(err).message,
    }),
  );
}

/** AuthCompleted — terminal success: the identity session exists and the user
 *  is authenticated. */
export function trackAuthCompleted(
  authMethod: AuthMethod,
  authType: AuthType,
): void {
  if (!ampli.isLoaded) return;

  ampli.track(new AuthCompleted(authContext(authMethod, authType)));
}

/** Firebase errors carry a string `code` ("auth/too-many-requests") that is not
 *  the {code,message} shape toApiError looks for, and it is the most useful
 *  discriminator this funnel has — it is what tells an abandoned consent screen
 *  from a rate-limited password. */
function firebaseErrorCode(err: unknown): string | undefined {
  if (err && typeof err === "object" && "code" in err) {
    const code = (err as { code: unknown }).code;
    if (typeof code === "string") return code;
  }

  return undefined;
}

/** UserLoginSucceeded — fired once a genuine login lands authenticated
 *  (session.tsx applyLogin). For the social-redirect return the method/type
 *  aren't recoverable from the URL, so the caller passes the GOOGLE/LOGIN
 *  default; the email path passes its known values. */
export function trackLoginSucceeded(
  authMethod?: AuthMethod,
  authType: AuthType = "LOGIN",
): void {
  if (!ampli.isLoaded) return;

  ampli.userLoginSucceeded({
    app: PRODUCT,
    authType,
    ...(authMethod ? { authMethod } : {}),
    ...(pageContext.page ? { page: pageContext.page } : {}),
  });
}

/** UserLoginFailed — fired when a login attempt fails (session.tsx /
 *  LoginScreen error path), including the email-not-verified case. */
export function trackLoginFailed(authMethod: AuthMethod, err: unknown): void {
  if (!ampli.isLoaded) return;

  const api: ApiError | undefined = toApiError(err);

  ampli.userLoginFailed({
    app: PRODUCT,
    authMethod,
    authType: "LOGIN",
    errorMessage: friendlyError(err).message,
    ...(api?.code ? { errorCode: api.code } : {}),
    ...(pageContext.page ? { page: pageContext.page } : {}),
  });
}

/** UserSignUpSucceeded — fired when a sign-up completes: the email path
 *  resolves on-screen, and the social path is detected on the redirect return
 *  via Firebase's isNewUser flag (see session.tsx applyLogin). */
export function trackSignupSucceeded(authMethod: AuthMethod): void {
  // Marketing conversion (OPSS-249) — above the guard, see trackAgentCreated.
  // Note the email path fires this while the account is still unverified, so ad
  // platforms count sign-ups that may never verify; accepted deliberately.
  pushGtmEvent("SignupSuccessful", { authMethod });

  if (!ampli.isLoaded) return;

  ampli.userSignUpSucceeded({
    app: PRODUCT,
    authMethod,
    authType: "SIGN_UP",
    ...(pageContext.page ? { page: pageContext.page } : {}),
  });
}

/** UserSignUpFailed — fired when a sign-up attempt fails. */
export function trackSignupFailed(authMethod: AuthMethod, err: unknown): void {
  if (!ampli.isLoaded) return;

  const api: ApiError | undefined = toApiError(err);

  ampli.userSignUpFailed({
    app: PRODUCT,
    authMethod,
    authType: "SIGN_UP",
    errorMessage: friendlyError(err).message,
    ...(api?.code ? { errorCode: api.code } : {}),
    ...(pageContext.page ? { page: pageContext.page } : {}),
  });
}

/** LogoutButtonPressed — fired when the user clicks sign out (SidebarUser).
 *
 *  Pins the current user_id onto the event via EventOptions. Logout fires just
 *  before resetUser() (SidebarUser.handleSignOut → logout), and the SDK stamps
 *  the ambient user_id during its async enrichment pipeline — so a synchronous
 *  reset() clearing the user_id can win that race and flush this event under the
 *  reset (anonymous) id. Carrying the id on the event keeps it attributed to the
 *  user who signed out, regardless of when it's flushed. */
export function trackLogoutPressed(buttonText: string): void {
  if (!ampli.isLoaded) return;

  const userId = ampli.client.getUserId();

  ampli.logoutButtonPressed(
    {
      app: PRODUCT,
      host: window.location.host,
      buttonText,
      workspaceId: ws(),
    },
    userId ? { user_id: userId } : undefined,
  );
}

// ---- workspace --------------------------------------------------------------

/** WorkspaceSwitched — fired when the user switches active workspace. */
export function trackWorkspaceSwitched(
  prevWorkspaceId: string,
  currWorkspaceId: string,
): void {
  if (!ampli.isLoaded) return;

  ampli.workspaceSwitched({
    app: PRODUCT,
    host: window.location.host,
    prevWorkspaceId,
    currWorkspaceId,
  });
}

/** WorkspaceCreated — fired when the user creates a new workspace. */
export function trackWorkspaceCreated(workspaceId: string): void {
  if (!ampli.isLoaded) return;

  ampli.workspaceCreated({ app: PRODUCT, workspaceId });
}

// ---- members ----------------------------------------------------------------

/** InviteMemberSent — fired on a member invite or a resend. */
export function trackInviteMemberSent(method: "memberInvite" | "resend"): void {
  if (!ampli.isLoaded) return;

  ampli.inviteMemberSent({
    app: PRODUCT,
    method,
    origin: "organizationAccountPage",
  });
}

// ---- API keys ---------------------------------------------------------------

/* The API-keys page is cross-workspace (keys grouped per workspace), so these
 * take the key's own workspaceId rather than the active-workspace ws() — the
 * acted-on key may belong to a workspace other than the one in the URL. */

/** APIKeyCreated — fired when a workspace API key is created. */
export function trackApiKeyCreated(workspaceId: string): void {
  if (!ampli.isLoaded) return;

  ampli.apiKeyCreated({ app: PRODUCT, workspaceId });
}

/** APIKeyDeleted — fired when a workspace API key is revoked. */
export function trackApiKeyDeleted(workspaceId: string): void {
  if (!ampli.isLoaded) return;

  ampli.apiKeyDeleted({ app: PRODUCT, workspaceId });
}

/** APIKeyActionPressed — fired on a non-destructive key action (rotate/rename),
 *  distinguished by `action`. */
export function trackApiKeyActionPressed(
  action: "rotate" | "rename",
  workspaceId: string,
): void {
  if (!ampli.isLoaded) return;

  ampli.apiKeyActionPressed({
    app: PRODUCT,
    host: window.location.host,
    action,
    workspaceId,
  });
}

// ---- errors -----------------------------------------------------------------

/** Map the app's string ApiError.code to the HTTP status the ApiRequestFailed
 *  schema expects (errorCode: integer). Network/unknown errors → 0. */
export function httpStatusForCode(code: ApiErrorCode | undefined): number {
  switch (code) {
    case "bad_request":
      return 400;
    case "unauthorized":
      return 401;
    case "forbidden":
      return 403;
    case "not_found":
      return 404;
    case "window_too_large":
      return 422;
    case "upstream":
      return 502;
    default:
      return 0;
  }
}

/** ApiRequestFailed — fired on any API load/mutation error. `origin` is the
 *  operation discriminator (see the spec's enum). Pulls the contract ApiError
 *  out of the thrown value for the status code and user-facing message. */
/** HTTP-ish status for a thrown API error (0 when unknown). Shared by the
 *  analytics funnel and the latency metrics so both label a failure the same. */
export function apiStatus(err: unknown): number {
  return httpStatusForCode(toApiError(err)?.code);
}

export function trackApiFailed(origin: string, err: unknown): void {
  const status = apiStatus(err);

  // OTEL error log — emitted independently of Amplitude so API failures reach
  // Loki/Tempo even when product analytics is off (mock mode, tests, no key).
  // Attributes are low-cardinality and sliceable: origin + HTTP status + ws.
  logger.error(
    err instanceof Error ? err : new Error(friendlyError(err).message),
    "api request failed",
    {
      origin,
      status,
      workspaceId: ws(),
      ...(pageContext.page ? { page: pageContext.page } : {}),
      ...(pageContext.path ? { path: pageContext.path } : {}),
    },
  );

  if (!ampli.isLoaded) return;

  ampli.apiRequestFailed({
    app: PRODUCT,
    workspaceId: ws(),
    origin,
    errorCode: status,
    errorMessage: friendlyError(err).message,
    ...(pageContext.page ? { page: pageContext.page } : {}),
    ...(pageContext.path ? { path: pageContext.path } : {}),
  });
}

/** Active workspace id, or "" before one resolves (required-prop guard). */
function ws(): string {
  return getActiveWorkspaceId() ?? "";
}

/** Force the Amplitude SDK to send queued events now.
 *
 *  The browser SDK batches (flushIntervalMillis), so an event tracked
 *  immediately before a full-page navigation never leaves the tab — it is
 *  persisted and re-sent on the NEXT load instead. That is why AuthStarted for
 *  a Google sign-in only appeared once the user came back, and why a user who
 *  abandoned at the consent screen produced no event at all. Callers about to
 *  navigate away should await this first. */
export async function flushAnalytics(): Promise<void> {
  if (!ampli.isLoaded) return;

  await ampli.client.flush().promise;
}
